// Main entry point: game loop, state machine, initialization
import { at } from './utils.js';
import * as Input from './input.js';
import * as Touch from './touch.js';
import { Audio } from './audio.js';
import * as Effects from './effects.js';
import * as City from './city.js';
import { Render } from './render.js';
import { Lighting } from './lighting.js';
import * as Vehicles from './vehicles.js';
import * as Traffic from './traffic.js';
import { Missions } from './missions.js';
import { Hud, type HudButton, type HudButtonId } from './hud.js';
import * as Tips from './tips.js';

export type GameState = 'PLAYING' | 'PAUSED' | 'HELP';

interface PauseSelection {
  row: number;
  col: number;
}

function requireAmbulance(): Vehicles.Ambulance {
  const amb = Vehicles.ambulance;
  if (!amb) throw new Error('Vehicles.init must run before this point');
  return amb;
}

class GameSystem {
  state: GameState = 'PAUSED';
  hasStarted = false; // track if it's the first time the menu is shown
  private canvasEl: HTMLCanvasElement | null = null;
  private ctxEl: CanvasRenderingContext2D | null = null;
  private lastTime = 0;
  private accumulator = 0;
  pauseSel: PauseSelection = { row: 0, col: 0 }; // controller selection on pause menu
  helpFrom: GameState | null = null; // which state to return to from HELP
  private cursorTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly TICK_DT = 1 / 60;
  private readonly MAX_ACCUMULATE = 0.25;
  private readonly TOUCH_ZOOM = 1.5; // camera zoom once touch controls have been used

  private canvas(): HTMLCanvasElement {
    if (!this.canvasEl) throw new Error('Game.init must run before use');
    return this.canvasEl;
  }

  private ctx(): CanvasRenderingContext2D {
    if (!this.ctxEl) throw new Error('Game.init must run before use');
    return this.ctxEl;
  }

  init(): void {
    const el = document.getElementById('gameCanvas');
    if (!(el instanceof HTMLCanvasElement)) throw new Error('#gameCanvas is missing or not a canvas');
    this.canvasEl = el;
    const context = el.getContext('2d');
    if (!context) throw new Error('2D canvas context is not available');
    this.ctxEl = context;

    this.resize();
    window.addEventListener('resize', () => { this.resize(); });
    window.addEventListener('blur', () => {
      if (this.state === 'PLAYING') {
        this.state = 'PAUSED';
        this.pauseSel = { row: 0, col: 0 };
        this.showCursor();
        void Audio.ctx?.suspend();
      }
    });

    window.addEventListener('mousemove', () => { this.showCursor(); });
    this.showCursor();

    // Initialize all systems
    Input.init();
    Touch.init(this.canvas());
    Audio.init();
    Effects.init();
    City.init();
    Render.init(this.canvas());
    Lighting.init(this.canvas());
    Vehicles.init();
    Traffic.init();
    Missions.init();
    Hud.init();

    // Snap camera to ambulance start position
    const amb = requireAmbulance();
    Render.snapCamera(amb.worldX, amb.worldY);

    // Start loop
    this.lastTime = performance.now();
    requestAnimationFrame((t) => { this.loop(t); });
  }

  // Hide cursor after 3s of inactivity during gameplay
  showCursor(): void {
    document.body.style.cursor = '';
    if (this.cursorTimer !== null) clearTimeout(this.cursorTimer);
    if (this.state === 'PLAYING') {
      this.cursorTimer = setTimeout(() => { document.body.style.cursor = 'none'; }, 3000);
    }
  }

  private resize(): void {
    const canvas = this.canvas();
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const lightCanvas = Lighting.lightCanvas;
    if (lightCanvas) {
      lightCanvas.width = canvas.width;
      lightCanvas.height = canvas.height;
    }
  }

  private loop(now: number): void {
    requestAnimationFrame((t) => { this.loop(t); });

    let frameTime = (now - this.lastTime) / 1000;
    this.lastTime = now;
    frameTime = Math.min(frameTime, this.MAX_ACCUMULATE);
    this.accumulator += frameTime;

    // Fixed-timestep physics updates
    while (this.accumulator >= this.TICK_DT) {
      this.update(this.TICK_DT);
      this.accumulator -= this.TICK_DT;
    }

    const alpha = this.state === 'PAUSED' ? 1.0 : this.accumulator / this.TICK_DT;
    this.render(alpha);
  }

  private update(dt: number): void {
    // Shared updates (input must always be polled)
    Input.update();
    Touch.update();
    Hud.update(dt);

    if (this.state === 'HELP') {
      if (Input.cancelPress() || Input.confirmPress()) {
        this.state = this.helpFrom ?? 'PAUSED';
      }
    } else if (this.state === 'PAUSED') {
      if (Input.pauseToggle()) {
        this.state = 'PLAYING';
        this.hasStarted = true;
        void Audio.ctx?.resume();
        this.showCursor();
      }
      const nav = Input.getPauseNav();
      const sel = this.pauseSel;
      const mode = Vehicles.getPhysicsMode();

      // A vertical move lands on the row's first column, so a column index
      // from the three-wide DRIVE TYPE row cannot linger on a two-wide row.
      if (nav.up) {
        sel.row = (sel.row + 4) % 5;
        if (sel.row === 2 && mode !== 'realism') sel.row = (sel.row + 4) % 5;
        sel.col = 0;
      }
      if (nav.down) {
        sel.row = (sel.row + 1) % 5;
        if (sel.row === 2 && mode !== 'realism') sel.row = (sel.row + 1) % 5;
        sel.col = 0;
      }

      // Horizontal navigation logic
      if (sel.row === 1 || sel.row === 3) {
        if (nav.left) sel.col = 0;
        if (nav.right) sel.col = 1;
      } else if (sel.row === 2) {
        // Drive Type (FWD, RWD, 4WD)
        if (nav.left) sel.col = (sel.col + 2) % 3;
        if (nav.right) sel.col = (sel.col + 1) % 3;
      } else {
        sel.col = 0;
      }

      if (nav.confirm) {
        if (sel.row === 0) {
          this.state = 'PLAYING';
          this.hasStarted = true;
          void Audio.ctx?.resume();
          this.showCursor();
        } else if (sel.row === 1) {
          Vehicles.setPhysicsMode(sel.col === 0 ? 'arcade' : 'realism');
        } else if (sel.row === 2 && mode === 'realism') {
          const types: Vehicles.DriveType[] = ['fwd', 'rwd', '4wd'];
          Vehicles.setDriveType(at(types, sel.col));
        } else if (sel.row === 3) {
          Lighting.timeOfDay = sel.col === 0 ? 0.5 : 0.0;
        } else if (sel.row === 4) {
          this.helpFrom = 'PAUSED';
          this.state = 'HELP';
        }
      }
    } else {
      // this.state === 'PLAYING'
      if (Input.trafficOverlayToggle()) {
        Traffic.toggleShowOverlay();
      }
      if (Input.pauseToggle()) {
        this.state = 'PAUSED';
        this.pauseSel = { row: 0, col: 0 };
        this.showCursor();
        void Audio.ctx?.suspend();
      }
      Missions.update(dt);
      Traffic.update(dt);
      Tips.update(dt);

      const mstate = Missions.state;
      const zoneAction = mstate === 'LOADING' || mstate === 'DELIVERY';
      Vehicles.updateAmbulance(dt, zoneAction);

      const amb = requireAmbulance();
      // Phones are small: pull the camera in when touch controls are in use
      const targetZoom = Touch.usesTouch() ? this.TOUCH_ZOOM : 1.0;
      Render.updateCamera(amb.worldX, amb.worldY, targetZoom, this.TICK_DT);

      // Collisions
      Vehicles.checkCollisions();

      // Audio
      Audio.update();

      // Lighting
      Lighting.update(dt);

      // Visuals
      Effects.update(dt);
    }

    // Clear edge-triggered inputs for the next tick
    Input.endFrame();
  }

  private render(alpha: number): void {
    const ctx = this.ctx();

    // 1. Clear background
    Render.begin();

    // 2. Draw world elements (interpolated)
    Render.drawWorld(alpha);

    // 4. Draw mission zones
    Render.drawMissionZones(alpha);

    // 5. Draw UI overlays
    if (this.state === 'HELP') {
      this.drawHelpPage(ctx);
    } else if (this.state === 'PLAYING') {
      Hud.render(ctx);
      Tips.render(ctx);
      Touch.render(ctx);
    } else {
      // this.state === 'PAUSED'
      Hud.render(ctx);
      this.drawPauseMenu(ctx);
    }

    Hud.renderToasts(ctx);
  }

  private requireButton(id: HudButtonId): HudButton {
    const b = Hud.btn(id);
    if (!b) throw new Error(`missing HUD button ${id}`);
    return b;
  }

  private drawPauseMenu(ctx: CanvasRenderingContext2D): void {
    const cw = ctx.canvas.width;
    const ch = ctx.canvas.height;
    const s = Hud.uiScale(cw, ch);
    const mode = Vehicles.getPhysicsMode();

    ctx.save();
    ctx.scale(s, s);
    const lw = cw / s, lh = ch / s;
    ctx.fillStyle = 'rgba(0,0,0,0.75)';
    ctx.fillRect(0, 0, lw, lh);

    // Fit the whole menu group to the screen: scale about the centre so its
    // bounding box fills the most constraining axis (see Hud.menuScale).
    // Button hit-testing in Hud applies the inverse of this transform.
    const k = Hud.menuScale(lw, lh);
    ctx.translate(lw / 2, lh / 2);
    ctx.scale(k, k);
    ctx.translate(-lw / 2, -lh / 2);

    ctx.textAlign = 'center';
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 96px "Courier New"';
    ctx.fillText(this.hasStarted ? 'PAUSED' : 'RESCUE', lw / 2, lh / 2 - 520);

    // Resume prompt with inline menu-button icon
    ctx.font = '48px "Courier New"';
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.textAlign = 'left';
    const promptY = lh / 2 - 440;
    const partA = this.hasStarted ? 'PRESS ESC OR ' : 'PRESS ENTER OR ';
    const partB = this.hasStarted ? ' TO RESUME' : ' TO START';
    const iconR = 22;
    const wA = ctx.measureText(partA).width;
    const wB = ctx.measureText(partB).width;
    const totalPromptW = wA + iconR * 2 + 12 + wB;
    const promptX = lw / 2 - totalPromptW / 2;
    ctx.fillText(partA, promptX, promptY);
    const iconCX = promptX + wA + iconR + 6;
    const iconCY = promptY - 14;
    ctx.beginPath(); ctx.arc(iconCX, iconCY, iconR, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.6)'; ctx.lineWidth = 2; ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    const iw = iconR * 1.1, ih = Math.max(2, iconR * 0.18);
    for (let i = -1; i <= 1; i++) {
      ctx.fillRect(iconCX - iw / 2, iconCY + i * iconR * 0.38 - ih / 2, iw, ih);
    }
    ctx.fillText(partB, promptX + wA + iconR * 2 + 12, promptY);

    const btnW = 400, gap = 40;
    const totalW = btnW * 2 + gap;
    const bx = lw / 2 - totalW / 2;
    const sel = this.pauseSel;

    // Resume button (row 0, full width)
    const resumeBy = lh / 2 - 380;
    const btnResume = this.requireButton('resume');
    const oldLabel = btnResume.label;
    if (!this.hasStarted) btnResume.label = 'START';
    Hud.drawButton(ctx, btnResume, bx, resumeBy, false, sel.row === 0);
    btnResume.label = oldLabel;

    // Physics section (row 1)
    ctx.font = 'bold 52px "Courier New"';
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.textAlign = 'center';
    ctx.fillText('PHYSICS STYLE', lw / 2, lh / 2 - 175);

    const physBy = lh / 2 - 135;
    Hud.drawButton(ctx, this.requireButton('arcade'), bx, physBy, mode === 'arcade', sel.row === 1 && sel.col === 0);
    Hud.drawButton(ctx, this.requireButton('realism'), bx + btnW + gap, physBy, mode === 'realism', sel.row === 1 && sel.col === 1);

    // Drive Type section (row 2) - Only active in Realism mode
    const driveType = Vehicles.getDriveType();
    ctx.font = 'bold 52px "Courier New"';
    ctx.fillStyle = mode === 'realism' ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.15)';
    ctx.fillText('DRIVE TYPE', lw / 2, lh / 2 + 50);

    const driveBy = lh / 2 + 90;
    const dbW = 250, dbGap = 45;
    const dTotalW = dbW * 3 + dbGap * 2;
    const dbx = lw / 2 - dTotalW / 2;

    const fwdBtn = this.requireButton('fwd');
    const rwdBtn = this.requireButton('rwd');
    const awdBtn = this.requireButton('4wd');

    fwdBtn.w = dbW; rwdBtn.w = dbW; awdBtn.w = dbW;

    if (mode === 'realism') {
      Hud.drawButton(ctx, fwdBtn, dbx, driveBy, driveType === 'fwd', sel.row === 2 && sel.col === 0);
      Hud.drawButton(ctx, rwdBtn, dbx + dbW + dbGap, driveBy, driveType === 'rwd', sel.row === 2 && sel.col === 1);
      Hud.drawButton(ctx, awdBtn, dbx + (dbW + dbGap) * 2, driveBy, driveType === '4wd', sel.row === 2 && sel.col === 2);
    } else {
      ctx.globalAlpha = 0.3;
      Hud.drawButton(ctx, fwdBtn, dbx, driveBy, false, false);
      Hud.drawButton(ctx, rwdBtn, dbx + dbW + dbGap, driveBy, false, false);
      Hud.drawButton(ctx, awdBtn, dbx + (dbW + dbGap) * 2, driveBy, false, false);
      ctx.globalAlpha = 1.0;
    }

    // Time section (row 3)
    ctx.font = 'bold 52px "Courier New"';
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.fillText('TIME', lw / 2, lh / 2 + 260);

    const isDay = Lighting.timeOfDay > 0.25 && Lighting.timeOfDay < 0.75;
    const timeBy = lh / 2 + 300;
    Hud.drawButton(ctx, this.requireButton('day'), bx, timeBy, isDay, sel.row === 3 && sel.col === 0);
    Hud.drawButton(ctx, this.requireButton('night'), bx + btnW + gap, timeBy, !isDay, sel.row === 3 && sel.col === 1);

    // Help button (row 4, full width)
    const helpBy = lh / 2 + 450;
    Hud.drawButton(ctx, this.requireButton('help'), bx, helpBy, false, sel.row === 4);

    ctx.restore();
  }

  private drawHelpPage(ctx: CanvasRenderingContext2D): void {
    const cw = ctx.canvas.width;
    const ch = ctx.canvas.height;
    const s = Hud.uiScale(cw, ch);

    ctx.save();
    ctx.scale(s, s);
    const lw = cw / s, lh = ch / s;
    ctx.fillStyle = 'rgba(0,0,0,0.88)';
    ctx.fillRect(0, 0, lw, lh);

    // Fit the content block (title through BACK) to the screen on its most
    // constraining axis, centred. Hit-testing in Hud applies the inverse.
    const k = Hud.helpScale(lw, lh);
    ctx.translate(lw / 2, lh / 2);
    ctx.scale(k, k);
    ctx.translate(-lw / 2, -Hud.HELP_CY);

    ctx.textAlign = 'center';
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 80px "Courier New"';
    ctx.fillText('HOW TO PLAY', lw / 2, 100);

    // Rules
    const rules = [
      'Respond to dispatch calls and reach the patient in time.',
      'Stop next to the patient to load them into the ambulance.',
      'Rush the patient to the nearest hospital.',
      'Patient stability drops during transport — drive fast!',
      'A health bonus is added to your score on delivery.',
      'Time limits adapt to how fast you drive.',
    ];
    ctx.font = '34px "Courier New"';
    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    rules.forEach((line, i) => { ctx.fillText(line, lw / 2, 200 + i * 52); });

    // Divider
    ctx.strokeStyle = 'rgba(255,255,255,0.2)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(lw / 2 - 500, 560);
    ctx.lineTo(lw / 2 + 500, 560);
    ctx.stroke();

    // Controls — 3 columns: keyboard | action | gamepad
    const colY = 610;
    const rowH = 44;
    const midX = lw / 2;
    const kbX = midX - 260;
    const gpX = midX + 260;

    const rows = [
      { kb: 'WASD / ARROWS', action: 'Drive', gp: 'L-Stick / D-Pad' },
      { kb: 'SHIFT', action: 'Brake / Reverse', gp: 'LT' },
      { kb: '', action: 'Gas', gp: 'RT' },
      { kb: 'ENTER', action: 'Start', gp: 'A Button' },
      { kb: 'SPACE', action: 'Siren', gp: 'LB / RB' },
      { kb: 'ESC', action: 'Pause', gp: 'Menu' },
      { kb: 'ESC / ENTER', action: 'Back', gp: 'A / B' },
    ];

    ctx.font = 'bold 30px "Courier New"';
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.textAlign = 'right';
    ctx.fillText('KEYBOARD', kbX, colY);
    ctx.textAlign = 'left';
    ctx.fillText('GAMEPAD', gpX, colY);
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.fillText('ACTION', midX, colY);

    ctx.font = '28px "Courier New"';
    rows.forEach((row, i) => {
      const y = colY + 44 + i * rowH;
      ctx.fillStyle = 'rgba(255,255,255,0.45)';
      ctx.textAlign = 'right';
      ctx.fillText(row.kb, kbX, y);
      ctx.textAlign = 'left';
      ctx.fillText(row.gp, gpX, y);
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.fillText(row.action, midX, y);
    });

    // Touch controls, under the table
    const tableBottom = colY + 44 + rows.length * rowH; // just below the last row
    const touchY = tableBottom + 30;
    ctx.font = 'bold 30px "Courier New"';
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.textAlign = 'center';
    ctx.fillText('TOUCH', midX, touchY);
    const touchLines = [
      'Drag the joystick: outer ring drives toward your finger,',
      'middle ring backs away from it, centre stops.',
      'Tap with a second finger for the siren. Top button pauses.',
    ];
    ctx.font = '28px "Courier New"';
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    touchLines.forEach((line, i) => { ctx.fillText(line, midX, touchY + 44 + i * 40); });
    const touchBottom = touchY + 44 + (touchLines.length - 1) * 40 + 20;

    // BACK button: as wide as the divider, directly under the touch lines
    const backBtn = this.requireButton('help_back');
    const bbx = lw / 2 - backBtn.w / 2;
    const bby = touchBottom + 20;
    Hud.drawButton(ctx, backBtn, bbx, bby, false, true);

    ctx.restore();
  }
}

export const Game = new GameSystem();

document.addEventListener('DOMContentLoaded', () => { Game.init(); });
